import { useEffect, useMemo, useState } from "react";
import { useCrm } from "@/lib/crm";
import { useAuth } from "@/lib/auth";
import { eur, eur2, statoInfo, PORTFOLIO_STATI, portfolioRicavi, portfolioMargine, portfolioIncassato } from "@/lib/constants";
import { DiarioTab, IncassiTab, LavoriTab, DocumentiTab } from "@/components/PortfolioExtra";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AttivitaPanel } from "@/components/AttivitaPanel";
import { toast } from "sonner";
import { Plus, Briefcase, MapPin, User, TrendingUp, Trash2, Save, Tag, Package } from "lucide-react";

const emptyNew = { nome: "", disciplina: "", citta: "", referente: "", telefono: "", email: "", stato: "attiva", note: "", costi: 0 };

export default function Portfolio() {
  const { portfolio, api, refresh } = useCrm();
  const { isTitolare } = useAuth();
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState(emptyNew);
  const [detail, setDetail] = useState(null);

  const totals = useMemo(() => {
    const ricavi = portfolio.reduce((a, p) => a + portfolioRicavi(p), 0);
    const costi = portfolio.reduce((a, p) => a + (Number(p.costi) || 0), 0);
    const incassato = portfolio.reduce((a, p) => a + portfolioIncassato(p), 0);
    return { ricavi, costi, margine: ricavi - costi, count: portfolio.length, daIncassare: ricavi - incassato };
  }, [portfolio]);

  const createNew = async () => {
    if (!form.nome.trim()) return;
    await api.createPortfolio({ ...form, costi: Number(form.costi) || 0 });
    setNewOpen(false);
    setForm(emptyNew);
    await refresh();
    toast.success("Società aggiunta al portfolio");
  };

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">Portfolio</h1>
          <p className="text-slate-500 mt-1">Società clienti del titolare (separate dalla pipeline collaboratori).</p>
        </div>
        <Button data-testid="add-portfolio-button" style={{ display: isTitolare ? undefined : "none" }} onClick={() => { setForm(emptyNew); setNewOpen(true); }} className="rounded-full gap-1.5">
          <Plus className="h-4 w-4" /> Nuova società
        </Button>
      </div>

      <div className={`grid grid-cols-2 gap-4 ${isTitolare ? "lg:grid-cols-5" : "lg:grid-cols-4"}`}>
        <Card className="p-5"><div className="text-xs uppercase tracking-wider text-slate-400">Società</div><div className="text-3xl font-extrabold mt-1 tabular-nums text-slate-900 dark:text-white">{totals.count}</div></Card>
        {isTitolare && <Card className="p-5"><div className="text-xs uppercase tracking-wider text-slate-400">Ricavi</div><div className="text-3xl font-extrabold mt-1 tabular-nums text-sky-600">{eur(totals.ricavi)}</div></Card>}
        {isTitolare && <Card className="p-5"><div className="text-xs uppercase tracking-wider text-slate-400">Costi</div><div className="text-3xl font-extrabold mt-1 tabular-nums text-slate-500">{eur(totals.costi)}</div></Card>}
        {isTitolare && <Card className="p-5"><div className="text-xs uppercase tracking-wider text-slate-400">Margine</div><div className="text-3xl font-extrabold mt-1 tabular-nums text-emerald-600">{eur(totals.margine)}</div></Card>}
        {isTitolare && <Card className="p-5" data-testid="portfolio-da-incassare"><div className="text-xs uppercase tracking-wider text-slate-400">Da incassare</div><div className="text-3xl font-extrabold mt-1 tabular-nums text-rose-600">{eur(totals.daIncassare)}</div></Card>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {portfolio.map((p) => {
          const info = statoInfo(p.stato);
          const ric = portfolioRicavi(p);
          const mar = portfolioMargine(p);
          return (
            <Card key={p.id} data-testid={`portfolio-card-${p.id}`}
              onClick={() => setDetail(p)}
              className="p-5 cursor-pointer hover:-translate-y-0.5 transition-transform duration-200 animate-fade-up">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="h-10 w-10 rounded-xl bg-slate-900 dark:bg-slate-700 flex items-center justify-center">
                    <Briefcase className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 dark:text-white">{p.nome}</div>
                    <div className="text-xs text-slate-400">{p.disciplina || "—"}</div>
                  </div>
                </div>
                <Badge variant="outline" className={info.chip}>{info.label}</Badge>
              </div>
              <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-500">
                {p.citta && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{p.citta}</span>}
                {p.referente && <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{p.referente}</span>}
              </div>
              {isTitolare && (
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <div className="rounded-lg bg-slate-50 dark:bg-slate-900/50 p-2.5">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Ricavi</div>
                    <div className="text-base font-bold text-sky-600 tabular-nums">{eur(ric)}</div>
                  </div>
                  <div className="rounded-lg bg-emerald-50 dark:bg-emerald-500/10 p-2.5">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-600/80">Margine</div>
                    <div className="text-base font-bold text-emerald-600 tabular-nums">{eur(mar)}</div>
                  </div>
                </div>
              )}
              {isTitolare && (ric - portfolioIncassato(p)) > 0 && (
                <div data-testid={`portfolio-card-da-incassare-${p.id}`} className="mt-2 text-xs text-rose-600 font-semibold">Da incassare: {eur(ric - portfolioIncassato(p))}</div>
              )}
            </Card>
          );
        })}
        {portfolio.length === 0 && (
          <div className="col-span-full rounded-xl border border-dashed border-slate-200 dark:border-slate-800 p-10 text-center text-sm text-slate-400">
            Nessuna società nel portfolio. Aggiungine una con "Nuova società".
          </div>
        )}
      </div>

      {/* New portfolio dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="portfolio-new-dialog">
          <DialogHeader>
            <DialogTitle>Nuova società (Portfolio)</DialogTitle>
            <DialogDescription>Cliente gestito direttamente dal titolare.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-1">
            <div className="grid gap-1.5"><Label>Nome società *</Label><Input data-testid="portfolio-nome-input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Disciplina</Label><Input value={form.disciplina} onChange={(e) => setForm({ ...form, disciplina: e.target.value })} /></div>
              <div className="grid gap-1.5"><Label>Città</Label><Input value={form.citta} onChange={(e) => setForm({ ...form, citta: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Referente</Label><Input value={form.referente} onChange={(e) => setForm({ ...form, referente: e.target.value })} /></div>
              <div className="grid gap-1.5"><Label>Telefono</Label><Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="grid gap-1.5">
                <Label>Stato</Label>
                <Select value={form.stato} onValueChange={(v) => setForm({ ...form, stato: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PORTFOLIO_STATI.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-1.5"><Label>Costi iniziali (€)</Label><Input type="number" value={form.costi} onChange={(e) => setForm({ ...form, costi: e.target.value })} /></div>
            <div className="grid gap-1.5"><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Annulla</Button>
            <Button data-testid="portfolio-create-submit" onClick={createNew}>Crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {detail && <PortfolioDetail p={detail} onClose={() => setDetail(null)} readOnly={!isTitolare} />}
    </div>
  );
}

function PortfolioDetail({ p, onClose, readOnly }) {
  const { api, refresh, portfolio } = useCrm();
  const fresh = portfolio.find((x) => x.id === p.id) || p;
  const [anag, setAnag] = useState(fresh);
  const [listino, setListino] = useState(fresh.listino || []);
  const [prodotti, setProdotti] = useState(fresh.prodotti || []);
  const [incassi, setIncassi] = useState(fresh.incassi || []);
  const [lavori, setLavori] = useState(fresh.lavori_stato || []);

  useEffect(() => {
    const f = portfolio.find((x) => x.id === p.id);
    if (f) { setAnag(f); setListino(f.listino || []); setProdotti(f.prodotti || []); setIncassi(f.incassi || []); setLavori(f.lavori_stato || []); }
  }, [portfolio, p.id]);

  const ricavi = prodotti.reduce((a, x) => a + (Number(x.prezzo) || 0) * (Number(x.quantita) || 0), 0);
  const margine = ricavi - (Number(anag.costi) || 0);

  const save = async () => {
    await api.updatePortfolio(p.id, {
      nome: anag.nome, disciplina: anag.disciplina, citta: anag.citta, referente: anag.referente,
      telefono: anag.telefono, email: anag.email, stato: anag.stato, note: anag.note,
      costi: Number(anag.costi) || 0,
      listino: listino.map(({ id, ...r }) => ({ ...r, prezzo: Number(r.prezzo) || 0 })),
      prodotti: prodotti.map(({ id, ...r }) => ({ ...r, prezzo: Number(r.prezzo) || 0, quantita: Number(r.quantita) || 0 })),
      incassi: incassi.map((r) => ({ ...r, importo: Number(r.importo) || 0 })),
      lavori_stato: lavori,
    });
    await refresh();
    toast.success("Società aggiornata");
    onClose();
  };
  const remove = async () => {
    if (!window.confirm(`Eliminare "${anag.nome}" dal Portfolio?`)) return;
    await api.deletePortfolio(p.id);
    await refresh();
    toast.success("Società eliminata");
    onClose();
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="portfolio-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-primary" /> {anag.nome}</DialogTitle>
          <DialogDescription>Gestione completa della società: dati, listino, prodotti e programmazione.</DialogDescription>
        </DialogHeader>

        {!readOnly && (
          <div className="grid grid-cols-3 gap-3 mb-2">
            <div className="rounded-lg bg-slate-50 dark:bg-slate-900/50 p-3"><div className="text-[10px] uppercase tracking-wider text-slate-400">Ricavi</div><div className="text-lg font-bold text-sky-600 tabular-nums">{eur2(ricavi)}</div></div>
            <div className="rounded-lg bg-slate-50 dark:bg-slate-900/50 p-3"><div className="text-[10px] uppercase tracking-wider text-slate-400">Costi</div><div className="text-lg font-bold text-slate-500 tabular-nums">{eur2(Number(anag.costi) || 0)}</div></div>
            <div className="rounded-lg bg-emerald-50 dark:bg-emerald-500/10 p-3"><div className="text-[10px] uppercase tracking-wider text-emerald-600/80">Margine</div><div className="text-lg font-bold text-emerald-600 tabular-nums">{eur2(margine)}</div></div>
          </div>
        )}

        <Tabs defaultValue="dati" className="w-full">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="dati" data-testid="portfolio-tab-dati">Dati</TabsTrigger>
            {!readOnly && <TabsTrigger value="listino" data-testid="portfolio-tab-listino">Listino dedicato</TabsTrigger>}
            {!readOnly && <TabsTrigger value="prodotti" data-testid="portfolio-tab-prodotti">Prodotti/Servizi</TabsTrigger>}
            <TabsTrigger value="programmazione" data-testid="portfolio-tab-programmazione">Programmazione</TabsTrigger>
            {!readOnly && <TabsTrigger value="diario" data-testid="portfolio-tab-diario">Diario</TabsTrigger>}
            {!readOnly && <TabsTrigger value="incassi" data-testid="portfolio-tab-incassi">Incassi</TabsTrigger>}
            {!readOnly && <TabsTrigger value="lavori" data-testid="portfolio-tab-lavori">Stato lavori</TabsTrigger>}
            <TabsTrigger value="documenti" data-testid="portfolio-tab-documenti">Documenti</TabsTrigger>
          </TabsList>

          <TabsContent value="dati" className="space-y-3 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Nome</Label><Input value={anag.nome} onChange={(e) => setAnag({ ...anag, nome: e.target.value })} /></div>
              <div className="grid gap-1.5"><Label>Disciplina</Label><Input value={anag.disciplina || ""} onChange={(e) => setAnag({ ...anag, disciplina: e.target.value })} /></div>
              <div className="grid gap-1.5"><Label>Città</Label><Input value={anag.citta || ""} onChange={(e) => setAnag({ ...anag, citta: e.target.value })} /></div>
              <div className="grid gap-1.5"><Label>Referente</Label><Input value={anag.referente || ""} onChange={(e) => setAnag({ ...anag, referente: e.target.value })} /></div>
              <div className="grid gap-1.5"><Label>Telefono</Label><Input value={anag.telefono || ""} onChange={(e) => setAnag({ ...anag, telefono: e.target.value })} /></div>
              <div className="grid gap-1.5"><Label>Email</Label><Input value={anag.email || ""} onChange={(e) => setAnag({ ...anag, email: e.target.value })} /></div>
              <div className="grid gap-1.5">
                <Label>Stato</Label>
                <Select value={anag.stato} onValueChange={(v) => setAnag({ ...anag, stato: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PORTFOLIO_STATI.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {!readOnly && <div className="grid gap-1.5"><Label>Costi (€)</Label><Input type="number" value={anag.costi} onChange={(e) => setAnag({ ...anag, costi: e.target.value })} /></div>}
            </div>
            <div className="grid gap-1.5"><Label>Note</Label><Textarea value={anag.note || ""} onChange={(e) => setAnag({ ...anag, note: e.target.value })} rows={3} /></div>
          </TabsContent>

          {!readOnly && (
          <TabsContent value="listino" className="space-y-2 mt-4">
            {listino.map((it, i) => (
              <div key={it.id || i} className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-primary flex-shrink-0" />
                <Input value={it.nome} onChange={(e) => setListino(listino.map((x, j) => j === i ? { ...x, nome: e.target.value } : x))} placeholder="Voce" className="flex-1" />
                <div className="relative w-28">
                  <Input type="number" value={it.prezzo} onChange={(e) => setListino(listino.map((x, j) => j === i ? { ...x, prezzo: e.target.value } : x))} className="pr-7 text-right tabular-nums" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                </div>
                <button onClick={() => setListino(listino.filter((_, j) => j !== i))} className="text-slate-300 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <Button variant="outline" className="rounded-full gap-1.5 border-dashed" onClick={() => setListino([...listino, { id: `n-${Date.now()}`, categoria: "Generale", nome: "Nuova voce", prezzo: 0 }])}>
              <Plus className="h-4 w-4" /> Aggiungi voce
            </Button>
          </TabsContent>
          )}

          {!readOnly && (
          <TabsContent value="prodotti" className="space-y-2 mt-4">
            {prodotti.map((it, i) => (
              <div key={it.id || i} className="flex items-center gap-2">
                <Package className="h-4 w-4 text-primary flex-shrink-0" />
                <Input value={it.nome} onChange={(e) => setProdotti(prodotti.map((x, j) => j === i ? { ...x, nome: e.target.value } : x))} placeholder="Prodotto/Servizio" className="flex-1" />
                <div className="relative w-24">
                  <Input type="number" value={it.prezzo} onChange={(e) => setProdotti(prodotti.map((x, j) => j === i ? { ...x, prezzo: e.target.value } : x))} className="pr-7 text-right tabular-nums" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                </div>
                <Input type="number" value={it.quantita} onChange={(e) => setProdotti(prodotti.map((x, j) => j === i ? { ...x, quantita: e.target.value } : x))} className="w-20 text-right tabular-nums" placeholder="Qtà" />
                <button onClick={() => setProdotti(prodotti.filter((_, j) => j !== i))} className="text-slate-300 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <Button variant="outline" className="rounded-full gap-1.5 border-dashed" onClick={() => setProdotti([...prodotti, { id: `n-${Date.now()}`, nome: "Nuovo prodotto", prezzo: 0, quantita: 1 }])}>
              <Plus className="h-4 w-4" /> Aggiungi prodotto
            </Button>
          </TabsContent>
          )}

          <TabsContent value="programmazione" className="mt-4">
            <AttivitaPanel fixedSsd={{ id: p.id, tipo: "portfolio", nome: anag.nome, email: anag.email }} canInvite />
          </TabsContent>
          {!readOnly && <TabsContent value="diario" className="mt-4"><DiarioTab p={fresh} /></TabsContent>}
          {!readOnly && <TabsContent value="incassi" className="mt-4"><IncassiTab incassi={incassi} setIncassi={setIncassi} ricavi={ricavi} /></TabsContent>}
          {!readOnly && <TabsContent value="lavori" className="mt-4"><LavoriTab lavori={lavori} setLavori={setLavori} /></TabsContent>}
          <TabsContent value="documenti" className="mt-4"><DocumentiTab p={fresh} readOnly={readOnly} /></TabsContent>
        </Tabs>

        <DialogFooter className="mt-2" style={{ display: readOnly ? "none" : undefined }}>
          <Button variant="outline" className="gap-1.5 text-destructive hover:text-destructive" onClick={remove} data-testid="portfolio-delete-button"><Trash2 className="h-4 w-4" /> Elimina</Button>
          <Button onClick={save} className="gap-1.5" data-testid="portfolio-save-button"><Save className="h-4 w-4" /> Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
